import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Container,
  Paper,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  Dialog,
  DialogContent,
  DialogActions,
  Fade,
  Grow
} from '@mui/material';
import { Helmet } from 'react-helmet';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SchoolIcon from '@mui/icons-material/School';
import PersonIcon from '@mui/icons-material/Person';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import PhoneIcon from '@mui/icons-material/Phone';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DoneIcon from '@mui/icons-material/Done';
import { styled, keyframes } from '@mui/material/styles';
import { getDownloadURL, listAll, ref } from 'firebase/storage';
import { storage } from '../../../firebase/firebase';
import { createUserWithEmailAndPassword, sendEmailVerification } from 'firebase/auth';
import { authen } from '../../../firebase/firebase';
import axios from 'axios';

// Components
import Navbar from '../../../components/Navbar/Navbar';
import Footer from '../../../components/Footer/Footer';
import Sidebar from '../../../components/Sidebar/Sidebar';
import DocumentViewerModal from '../../../components/DocumentViewerModal';
import CustomModal from '../../../components/CustomModal';

// Utilities
import RemoteErrorLog from '../../../Utils/api';
import { environment } from '../../../../environments/environment';

// Styles
import '../../../scss/Form.scss';

interface SchoolFormData {
  schoolName: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  adminEmail: string;
  adminPhone: string;
  adminUsername: string;
  password: string;
  confirmPassword: string;
  termsAgreed: boolean;
}

interface DocumentUrls {
  privacyPolicy: string | null;
  termsAndConditions: string | null;
}

// Floating animation (same as InitialChoice)
const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-6px); }
  100% { transform: translateY(0px); }
`;

const ContentContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: 800,
  margin: '0 auto',
  position: 'relative',
  
  // Subtle background for the content area
  '&::before': {
    content: '""',
    position: 'absolute',
    top: -20,
    left: -20,
    right: -20,
    bottom: -20,
    background: `
      radial-gradient(circle at 30% 70%, rgba(42, 146, 53, 0.03) 0%, transparent 50%),
      radial-gradient(circle at 70% 30%, rgba(76, 175, 80, 0.03) 0%, transparent 50%)
    `,
    borderRadius: 24,
    zIndex: -1,
  }
}));

const HeaderSection = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  marginBottom: theme.spacing(3),
}));

const MainTitle = styled(Typography)(({ theme }) => ({
  // match the provided snippet: responsive sizes, weight, gradient text and fallback
  fontSize: '1.75rem',
  fontWeight: 700,
  marginBottom: theme.spacing(1.5),
  letterSpacing: '-0.02em',
  position: 'relative',
  background: 'linear-gradient(135deg, #2A9235 0%, #4CAF50 100%)',
  WebkitBackgroundClip: 'text',
  backgroundClip: 'text',
  color: 'transparent',
  WebkitTextFillColor: theme.palette.text.secondary,

  [theme.breakpoints.up('md')]: {
    fontSize: '2.25rem',
  },

  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: -8,
    left: '50%',
    transform: 'translateX(-50%)',
    width: 80,
    height: 3,
    background: 'linear-gradient(90deg, #2A9235, #4CAF50)',
    borderRadius: 2,
  }
}));

const SubTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1rem',
  fontWeight: 400,
  color: theme.palette.text.secondary,
  lineHeight: 1.6,
  marginTop: theme.spacing(0.5),
  marginBottom: theme.spacing(2),
  [theme.breakpoints.up('md')]: {
    fontSize: '1.125rem',
  }
}));

const FormSection = styled(Paper)(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(3, 2.5),
  borderRadius: 20,
  border: '1px solid rgba(42, 146, 53, 0.1)',
  background: `
    linear-gradient(145deg, 
      rgba(255, 255, 255, 0.95) 0%, 
      rgba(248, 250, 252, 0.95) 100%
    )
  `,
  backdropFilter: 'blur(10px)',
  overflow: 'hidden',
  transition: 'all 0.3s cubic-bezier(0.23, 1, 0.32, 1)',
  boxShadow: `
    0 8px 32px rgba(42, 146, 53, 0.08),
    0 4px 16px rgba(0, 0, 0, 0.04)
  `,
  marginBottom: theme.spacing(2),
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: 'linear-gradient(90deg, #2A9235, #4CAF50)',
    borderRadius: '20px 20px 0 0',
  },

  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(2.5, 2),
    borderRadius: 16,
  },
  
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2, 1.5),
    marginBottom: theme.spacing(1.5),
  }
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  fontWeight: 700,
  color: '#1a1a1a',
  marginBottom: theme.spacing(2.5),
  fontSize: 'clamp(1.1rem, 2vw, 1.3rem)',
  letterSpacing: '-0.01em',
  
  '& .section-icon': {
    marginRight: theme.spacing(1.2),
    padding: theme.spacing(0.6),
    borderRadius: '50%',
    backgroundColor: 'rgba(42, 146, 53, 0.1)',
    fontSize: '1.1rem',
    color: '#2A9235',
    animation: `${float} 6s ease-in-out infinite`,
    border: '2px solid rgba(42, 146, 53, 0.1)',
  }
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 14,
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    transition: 'all 0.3s cubic-bezier(0.23, 1, 0.32, 1)',
    fontSize: '0.9rem',
    minHeight: '46px',
    border: '1px solid rgba(42, 146, 53, 0.1)',
    
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      borderColor: 'rgba(42, 146, 53, 0.3)',
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 12px rgba(42, 146, 53, 0.08)',
      
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: 'rgba(42, 146, 53, 0.3)',
      }
    },
    
    '&.Mui-focused': {
      backgroundColor: 'rgba(255, 255, 255, 1)',
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 20px rgba(42, 146, 53, 0.12)',
      
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#2A9235',
        borderWidth: '2px',
      }
    }
  },
  
  '& .MuiInputLabel-root': {
    fontSize: '0.9rem',
    fontWeight: 500,
    '&.Mui-focused': {
      color: '#2A9235',
    }
  },
  
  '& .MuiFormHelperText-root': {
    fontSize: '0.75rem',
    marginTop: theme.spacing(0.3),
    marginLeft: theme.spacing(0.8),
  }
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: 14,
  padding: theme.spacing(1.4, 4),
  fontSize: '0.95rem',
  fontWeight: 600,
  textTransform: 'none',
  background: 'linear-gradient(135deg, rgba(42, 146, 53, 0.9) 0%, rgba(76, 175, 80, 0.9) 100%)',
  color: 'white',
  border: 'none',
  minHeight: 46,
  transition: 'all 0.4s cubic-bezier(0.23, 1, 0.32, 1)',
  boxShadow: '0 6px 20px rgba(42, 146, 53, 0.2)',
  position: 'relative',
  overflow: 'hidden',
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
    transition: 'left 0.5s ease',
  },
  
  '&:hover:not(:disabled)': {
    background: 'linear-gradient(135deg, #2A9235 0%, #4CAF50 100%)',
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 24px rgba(42, 146, 53, 0.3)',
    
    '&::before': {
      left: '100%',
    }
  },
  
  '&:disabled': {
    background: 'rgba(0, 0, 0, 0.12)',
    color: 'rgba(0, 0, 0, 0.26)',
    cursor: 'not-allowed',
    transform: 'none',
    boxShadow: 'none',
  },
  
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.9rem',
    padding: theme.spacing(1.2, 3.5),
    minHeight: 44,
  }
}));

const BackButton = styled(Button)(({ theme }) => ({
  color: '#2A9235',
  textTransform: 'none',
  fontWeight: 600,
  padding: theme.spacing(0.8, 1.5),
  borderRadius: 12,
  transition: 'all 0.3s ease',
  fontSize: '0.9rem',
  
  '&:hover': {
    backgroundColor: 'rgba(42, 146, 53, 0.08)',
    transform: 'translateX(-4px)',
  }
}));

const TermsSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2.5),
  borderRadius: 14,
  background: 'rgba(42, 146, 53, 0.03)',
  border: '1px solid rgba(42, 146, 53, 0.08)',
  marginBottom: theme.spacing(3),
  
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
  }
}));

const InputIconWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 0.8),
  color: '#2A9235',
  fontSize: '1rem',
}));

const SchoolRegistration: React.FC = () => {
  const navigate = useNavigate();
  const formRef = useRef<HTMLFormElement | null>(null);

  // Loading and submission states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Modal states
  const [privacyPolicyOpen, setPrivacyPolicyOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [emailVerificationModalOpen, setEmailVerificationModalOpen] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState('');

  // Success dialog states
  const [registeredSchoolId, setRegisteredSchoolId] = useState('');
  const [schoolIdCopied, setSchoolIdCopied] = useState(false);
  const [loginLinkCopied, setLoginLinkCopied] = useState(false);

  // Initial form state
  const initialFormState: SchoolFormData = {
    schoolName: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    adminEmail: '',
    adminPhone: '',
    adminUsername: '',
    password: '',
    confirmPassword: '',
    termsAgreed: false
  };

  // Form state
  const [formData, setFormData] = useState<SchoolFormData>(initialFormState);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Document URLs for privacy policy and terms
  const [documentUrls, setDocumentUrls] = useState<DocumentUrls>({
    privacyPolicy: null,
    termsAndConditions: null
  });

  // Indian states list
  const INDIAN_STATES = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
    'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand',
    'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur',
    'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab',
    'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura',
    'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
    'Andaman and Nicobar Islands', 'Chandigarh',
    'Dadra and Nagar Haveli and Daman and Diu', 'Delhi',
    'Jammu and Kashmir', 'Ladakh', 'Lakshadweep', 'Puducherry'
  ];

  // Fetch policy documents from Firebase Storage
  const fetchPages = async () => {
    try {
      const storageRef = ref(storage, 'pages');
      const result = await listAll(storageRef);
      const urls = await Promise.all(
        result.items.map((pages) => getDownloadURL(pages))
      );

      setDocumentUrls({
        termsAndConditions: urls[2] || null,
        privacyPolicy: urls[1] || null
      });
    } catch (error) {
      RemoteErrorLog(error, 'SchoolRegistration.tsx');
    }
  };

  useEffect(() => {
    fetchPages();
    window.scrollTo(0, 0);
  }, []);

  // Navigation handlers
  const handleBack = () => {
    navigate('/signup-school-select');
  };

  // Reset form function
  const resetForm = () => {
    setFormData(initialFormState);
    setErrors({});
    formRef.current?.reset();
  };

  // Copy to clipboard function
  const handleCopy = async (text: string, setCopied: (value: boolean) => void) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  // Input change handler
  const handleInputChange = (field: keyof SchoolFormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: '',
      });
    }
  };

  // Validation function
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    let isValid = true;

    if (!formData.schoolName.trim()) {
      newErrors.schoolName = 'School name is required';
      isValid = false;
    }

    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
      isValid = false;
    }

    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
      isValid = false;
    }

    if (!formData.state.trim()) {
      newErrors.state = 'State is required';
      isValid = false;
    }

    if (!formData.zipCode.trim()) {
      newErrors.zipCode = 'Zip code is required';
      isValid = false;
    } else if (!/^\d{6}$/.test(formData.zipCode)) {
      newErrors.zipCode = 'Zip code must be 6 digits';
      isValid = false;
    }

    if (!formData.adminEmail.trim()) {
      newErrors.adminEmail = 'Email is required';
      isValid = false;
    } else if (!/^\S+@\S+\.\S+$/.test(formData.adminEmail)) {
      newErrors.adminEmail = 'Invalid email format';
      isValid = false;
    }

    if (!formData.adminUsername.trim()) {
      newErrors.adminUsername = 'Username is required';
      isValid = false;
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
      isValid = false;
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
      isValid = false;
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
      isValid = false;
    }

    if (!formData.termsAgreed) {
      newErrors.termsAgreed = 'You must agree to the terms and conditions';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  // Form submission handler
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitError(null);

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Create Firebase user
      const userCredential = await createUserWithEmailAndPassword(
        authen,
        formData.adminEmail,
        formData.password
      );

      // Send email verification
      await sendEmailVerification(userCredential.user);

      // Get ID token
      const idToken = await userCredential.user.getIdToken();

      // Register school with backend
      const response = await axios.post(
        `${environment.backendUrl}/register-school`,
        {
          schoolName: formData.schoolName,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          zipCode: formData.zipCode,
          adminEmail: formData.adminEmail,
          adminPhone: formData.adminPhone || undefined,
          adminUsername: formData.adminUsername,
          firebaseUid: userCredential.user.uid
        },
        {
          headers: {
            Authorization: `Bearer ${idToken}`
          }
        }
      );

      if (response.data.status === 'success') {
        setRegisteredSchoolId(response.data.school_id);
        resetForm();
        setSuccessDialogOpen(true);
      }

    } catch (error: any) {
      if (error.code === 'auth/email-already-in-use') {
        setErrorModalMessage('An account with this email already exists. Please use a different email address.');
        setErrorModalOpen(true);
      } else if (axios.isAxiosError(error)) {
        const errorData = error.response?.data;
        if (errorData?.error === 'School with these details already exists') {
          setErrorModalMessage(errorData.error);
          setErrorModalOpen(true);
        } else {
          setSubmitError(errorData?.error || 'Failed to register school');
        }
        RemoteErrorLog(error, 'SchoolRegistration.tsx');
      } else {
        setSubmitError('An unexpected error occurred');
        RemoteErrorLog(error, 'SchoolRegistration.tsx');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Modal handlers
  const handleSuccessDialogClose = () => {
    setSuccessDialogOpen(false);
    setEmailVerificationModalOpen(true);
  };

  const handlePolicyClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (documentUrls.privacyPolicy) {
      setPrivacyPolicyOpen(true);
    }
  };

  const handleTermsClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (documentUrls.termsAndConditions) {
      setTermsOpen(true);
    }
  };

  const isFormValid = () => {
    return formData.schoolName.trim() &&
      formData.address.trim() &&
      formData.city.trim() &&
      formData.state.trim() &&
      formData.zipCode.trim() &&
      formData.adminEmail.trim() &&
      formData.adminUsername.trim() &&
      formData.password.trim() &&
      formData.confirmPassword.trim() &&
      formData.password === formData.confirmPassword &&
      formData.password.length >= 8 &&
      /^\S+@\S+\.\S+$/.test(formData.adminEmail) &&
      /^\d{6}$/.test(formData.zipCode) &&
      formData.termsAgreed;
  };

  return (
    <>
      <Helmet>
        <title>Register Your School - Spelling Hero</title>
        <meta name="description" content="Register your school with Spelling Hero and create your administrator account to get started with educational spelling games." />
        <meta name="keywords" content="school registration, admin account, educational platform, spelling games for schools" />
      </Helmet>

      {/* Loading Overlay */}
      {isSubmitting && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            bgcolor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
            flexDirection: 'column'
          }}
        >
          <CircularProgress size={60} sx={{ color: '#2A9235', mb: 2 }} />
          <Typography variant="h6" sx={{ color: 'white' }}>
            Registering your school...
          </Typography>
        </Box>
      )}

      <header>
        <Navbar currentPage="signup" />
      </header>

      <main className="main-container" style={{ paddingTop: 0 }}>
        <section className="quiz-signup">
          <Box sx={{ padding: 0 }}>
            <Grid container spacing={2}>
              <Grid
                item
                lg={5}
                xs={12}
                sm={12}
                sx={{ 
                  paddingLeft: 0,
                  display: { xs: 'none', lg: 'block' }
                }}
                className="sidebar-top-spacing"
              >
                <Sidebar />
              </Grid>

              <Grid item xs={12} sm={12} lg={7} xl={7}>
                <Box sx={{
                  position: 'relative',
                  paddingLeft: { xs: 1.5, sm: 2, md: 3 },
                  paddingRight: { xs: 1.5, sm: 2, md: 3 },
                  paddingTop: 1.5,
                  paddingBottom: 2,
                  marginTop: { xs: 0.5, md: 1.5 },
                  maxWidth: '100%',
                  overflow: 'hidden'
                }}>
                  {/* Back Button - positioned close to sidebar (match SchoolOptions) */}
                  <Box sx={{ 
                    position: 'absolute', 
                    top: { xs: 4, md: 8 }, 
                    left: { xs: 4, md: 8 }, 
                    zIndex: 1 
                  }}>
                    <BackButton
                      startIcon={<ArrowBackIcon />}
                      onClick={handleBack}
                      disabled={isSubmitting}
                    >
                      Back
                    </BackButton>
                  </Box>

                  <ContentContainer>
                    <Fade in={true} timeout={800}>
                      <HeaderSection>
                        <MainTitle variant="h2">
                          Register Your School
                        </MainTitle>
                        <SubTitle variant="h5" sx={{ px: { xs: 2, md: 0 } }}>
                          Let's get your school and administrator account set up in just a few steps
                        </SubTitle>
                      </HeaderSection>
                    </Fade>

                    <form ref={formRef} onSubmit={handleSubmit}>
                      {/* School Information Section */}
                      <Grow in={true} timeout={1000}>
                        <FormSection elevation={0}>
                          <SectionTitle variant="h4">
                            <SchoolIcon className="section-icon" />
                            School Information
                          </SectionTitle>

                          <Grid container spacing={1.5}>
                            {/* Row 1: School Name and Address */}
                            <Grid item xs={12} md={6}>
                              <StyledTextField
                                fullWidth
                                label="School Name"
                                placeholder="Enter your school's full name"
                                value={formData.schoolName}
                                onChange={handleInputChange('schoolName')}
                                error={!!errors.schoolName}
                                helperText={errors.schoolName}
                                required
                                size="medium"
                                InputProps={{
                                  startAdornment: (
                                    <InputIconWrapper>
                                      <SchoolIcon />
                                    </InputIconWrapper>
                                  )
                                }}
                              />
                            </Grid>

                            <Grid item xs={12} md={6}>
                              <StyledTextField
                                fullWidth
                                label="School Address"
                                placeholder="Enter complete school address"
                                value={formData.address}
                                onChange={handleInputChange('address')}
                                error={!!errors.address}
                                helperText={errors.address}
                                required
                                size="medium"
                                InputProps={{
                                  startAdornment: (
                                    <InputIconWrapper>
                                      <LocationOnIcon />
                                    </InputIconWrapper>
                                  )
                                }}
                              />
                            </Grid>

                            {/* Row 2: City, State, ZIP Code */}
                            <Grid item xs={12} sm={6} md={5}>
                              <StyledTextField
                                fullWidth
                                label="City"
                                placeholder="Enter city"
                                value={formData.city}
                                onChange={handleInputChange('city')}
                                error={!!errors.city}
                                helperText={errors.city}
                                required
                                size="medium"
                                InputProps={{
                                  startAdornment: (
                                    <InputIconWrapper>
                                      <LocationOnIcon />
                                    </InputIconWrapper>
                                  )
                                }}
                              />
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                              <Autocomplete
                                options={INDIAN_STATES}
                                value={formData.state || ''}
                                onChange={(event, newValue) => {
                                  setFormData({
                                    ...formData,
                                    state: newValue || '',
                                  });
                                  if (errors.state) {
                                    setErrors({
                                      ...errors,
                                      state: '',
                                    });
                                  }
                                }}
                                renderInput={(params) => (
                                  <StyledTextField
                                    {...params}
                                    label="State"
                                    placeholder="Select state"
                                    error={!!errors.state}
                                    helperText={errors.state}
                                    required
                                    size="medium"
                                    InputProps={{
                                      ...params.InputProps,
                                      startAdornment: (
                                        <InputIconWrapper>
                                          <LocationOnIcon />
                                        </InputIconWrapper>
                                      )
                                    }}
                                  />
                                )}
                                fullWidth
                              />
                            </Grid>

                            <Grid item xs={12} sm={12} md={3}>
                              <StyledTextField
                                fullWidth
                                label="ZIP Code"
                                placeholder="6-digit PIN"
                                value={formData.zipCode}
                                onChange={handleInputChange('zipCode')}
                                error={!!errors.zipCode}
                                helperText={errors.zipCode}
                                required
                                inputProps={{ maxLength: 6 }}
                                size="medium"
                              />
                            </Grid>
                          </Grid>
                        </FormSection>
                      </Grow>

                      {/* Administrator Details Section */}
                      <Grow in={true} timeout={1200}>
                        <FormSection elevation={0}>
                          <SectionTitle variant="h4">
                            <PersonIcon className="section-icon" />
                            Administrator Details
                          </SectionTitle>

                          <Grid container spacing={1.5}>
                            <Grid item xs={12} md={8}>
                              <StyledTextField
                                fullWidth
                                label="Administrator Email"
                                type="email"
                                placeholder="admin@yourschool.edu"
                                value={formData.adminEmail}
                                onChange={handleInputChange('adminEmail')}
                                error={!!errors.adminEmail}
                                helperText={errors.adminEmail}
                                required
                                size="medium"
                                InputProps={{
                                  startAdornment: (
                                    <InputIconWrapper>
                                      <EmailIcon />
                                    </InputIconWrapper>
                                  )
                                }}
                              />
                            </Grid>

                            <Grid item xs={12} md={4}>
                              <StyledTextField
                                fullWidth
                                label="Phone Number"
                                placeholder="Phone (optional)"
                                value={formData.adminPhone}
                                onChange={handleInputChange('adminPhone')}
                                size="medium"
                                InputProps={{
                                  startAdornment: (
                                    <InputIconWrapper>
                                      <PhoneIcon />
                                    </InputIconWrapper>
                                  )
                                }}
                              />
                            </Grid>
                          </Grid>
                        </FormSection>
                      </Grow>

                      {/* Account Creation Section */}
                      <Grow in={true} timeout={1400}>
                        <FormSection elevation={0}>
                          <SectionTitle variant="h4">
                            <LockIcon className="section-icon" />
                            Create Admin Account
                          </SectionTitle>

                          <Grid container spacing={1.5}>
                            <Grid item xs={12} md={4}>
                              <StyledTextField
                                fullWidth
                                label="Username"
                                placeholder="Choose username"
                                value={formData.adminUsername}
                                onChange={handleInputChange('adminUsername')}
                                error={!!errors.adminUsername}
                                helperText={errors.adminUsername}
                                required
                                size="medium"
                                InputProps={{
                                  startAdornment: (
                                    <InputIconWrapper>
                                      <PersonIcon />
                                    </InputIconWrapper>
                                  )
                                }}
                              />
                            </Grid>

                            <Grid item xs={12} md={4}>
                              <StyledTextField
                                fullWidth
                                label="Password"
                                type="password"
                                placeholder="Create strong password"
                                value={formData.password}
                                onChange={handleInputChange('password')}
                                error={!!errors.password}
                                helperText={errors.password || "Minimum 8 characters"}
                                required
                                size="medium"
                                InputProps={{
                                  startAdornment: (
                                    <InputIconWrapper>
                                      <LockIcon />
                                    </InputIconWrapper>
                                  )
                                }}
                              />
                            </Grid>

                            <Grid item xs={12} md={4}>
                              <StyledTextField
                                fullWidth
                                label="Confirm Password"
                                type="password"
                                placeholder="Confirm your password"
                                value={formData.confirmPassword}
                                onChange={handleInputChange('confirmPassword')}
                                error={!!errors.confirmPassword}
                                helperText={errors.confirmPassword}
                                required
                                size="medium"
                                InputProps={{
                                  startAdornment: (
                                    <InputIconWrapper>
                                      <LockIcon />
                                    </InputIconWrapper>
                                  )
                                }}
                              />
                            </Grid>
                          </Grid>

                          {/* Terms and Conditions */}
                          <TermsSection>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={formData.termsAgreed}
                                  onChange={(e) => setFormData({ ...formData, termsAgreed: e.target.checked })}
                                  sx={{
                                    '&.Mui-checked': {
                                      color: '#2A9235',
                                    }
                                  }}
                                />
                              }
                              label={
                                <Typography variant="body2" sx={{ fontSize: { xs: '0.85rem', md: '0.9rem' }, lineHeight: 1.4 }}>
                                  I agree to the{' '}
                                  <Typography
                                    component="span"
                                    onClick={handlePolicyClick}
                                    sx={{
                                      color: '#2A9235',
                                      cursor: 'pointer',
                                      textDecoration: 'underline',
                                      fontWeight: 600,
                                      '&:hover': { color: '#1e7a2b' }
                                    }}
                                  >
                                    Privacy Policy
                                  </Typography>
                                  {' '}and{' '}
                                  <Typography
                                    component="span"
                                    onClick={handleTermsClick}
                                    sx={{
                                      color: '#2A9235',
                                      cursor: 'pointer',
                                      textDecoration: 'underline',
                                      fontWeight: 600,
                                      '&:hover': { color: '#1e7a2b' }
                                    }}
                                  >
                                    Terms & Conditions
                                  </Typography>
                                </Typography>
                              }
                            />
                            
                            {errors.termsAgreed && (
                              <Typography variant="caption" color="error" sx={{ ml: 4, display: 'block', mt: 0.5 }}>
                                {errors.termsAgreed}
                              </Typography>
                            )}
                          </TermsSection>

                          {/* Submit Error */}
                          {submitError && (
                            <Box sx={{ mb: 2, textAlign: 'center' }}>
                              <Typography variant="body2" color="error" sx={{ 
                                padding: 1.5, 
                                borderRadius: 2, 
                                backgroundColor: 'rgba(244, 67, 54, 0.1)',
                                border: '1px solid rgba(244, 67, 54, 0.2)',
                                fontSize: { xs: '0.8rem', md: '0.85rem' }
                              }}>
                                {submitError}
                              </Typography>
                            </Box>
                          )}

                          {/* Submit Button */}
                          <Box sx={{ textAlign: 'center', mt: 2 }}>
                            <ActionButton
                              type="submit"
                              size="medium"
                              disabled={!isFormValid() || isSubmitting}
                              sx={{
                                minWidth: { xs: '100%', sm: 240 },
                                fontSize: { xs: '0.9rem', md: '0.95rem' },
                                py: { xs: 1.2, md: 1.4 }
                              }}
                            >
                              {isSubmitting ? (
                                <>
                                  <CircularProgress size={18} sx={{ mr: 2, color: 'white' }} />
                                  Creating Your School...
                                </>
                              ) : (
                                'Complete Registration'
                              )}
                            </ActionButton>
                          </Box>
                        </FormSection>
                      </Grow>
                    </form>
                  </ContentContainer>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </section>
      </main>

      <Footer />

      {/* Document Viewer Modals */}
      <DocumentViewerModal
        open={privacyPolicyOpen}
        onClose={() => setPrivacyPolicyOpen(false)}
        documentUrl={documentUrls.privacyPolicy}
        title="Privacy Policy"
      />

      <DocumentViewerModal
        open={termsOpen}
        onClose={() => setTermsOpen(false)}
        documentUrl={documentUrls.termsAndConditions}
        title="Terms & Conditions"
      />

      {/* Success Dialog */}
      <Dialog
        open={successDialogOpen}
        onClose={handleSuccessDialogClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden'
          }
        }}
      >
        <DialogContent sx={{ textAlign: 'center', py: 4 }}>
          <CheckCircleIcon sx={{ fontSize: 80, color: 'green', mb: 2 }} />
          <Typography variant="h4" component="h2" sx={{ mb: 3, fontWeight: 'bold' }}>
            Registration Successful
          </Typography>

          <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold', color: '#2A9235' }}>
            SCHOOL ID
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
            <Typography variant="h5" sx={{ mr: 2, fontFamily: 'monospace', fontWeight: 'bold' }}>
              {registeredSchoolId}
            </Typography>
            <Button
              onClick={() => handleCopy(registeredSchoolId, setSchoolIdCopied)}
              startIcon={schoolIdCopied ? <DoneIcon /> : <ContentCopyIcon />}
              sx={{ color: schoolIdCopied ? 'green' : 'text.secondary' }}
            >
              {schoolIdCopied ? 'Copied' : 'Copy'}
            </Button>
          </Box>

          <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold', color: '#2A9235' }}>
            QUICK LOGIN LINK
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3, flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ mr: 2, wordBreak: 'break-all' }}>
              {`${window.location.origin}/login/school/${registeredSchoolId}`}
            </Typography>
            <Button
              onClick={() => handleCopy(`${window.location.origin}/login/school/${registeredSchoolId}`, setLoginLinkCopied)}
              startIcon={loginLinkCopied ? <DoneIcon /> : <ContentCopyIcon />}
              sx={{ color: loginLinkCopied ? 'green' : 'text.secondary', flexShrink: 0 }}
            >
              {loginLinkCopied ? 'Copied' : 'Copy'}
            </Button>
          </Box>

          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
            Please save this information for future login access.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button
            onClick={handleSuccessDialogClose}
            sx={{
              bgcolor: 'green',
              '&:hover': { bgcolor: 'darkgreen' },
              px: 3
            }}
            variant="contained"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Email Verification Modal */}
      <CustomModal
        open={emailVerificationModalOpen}
        onClose={() => setEmailVerificationModalOpen(false)}
        buttonText="OK"
        modalTitle="Verify your Email ID"
        text="Please check your email to proceed. We've sent a verification link to your email."
        icon={true}
      />

      {/* Error Modal */}
      <CustomModal
        open={errorModalOpen}
        onClose={() => setErrorModalOpen(false)}
        buttonText="OK"
        modalTitle="Registration Error"
        text={errorModalMessage}
        icon={true}
      />
    </>
  );
};

export default SchoolRegistration;
export type { SchoolFormData };
